import { yupResolver } from '@hookform/resolvers/yup';
import { DialogProps } from '@reach/dialog';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircleSolid } from 'components/icons';
import {
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  Flex,
  Input,
  Message,
  RadioBox,
  Stack,
  Text,
} from 'components/ui';
import { useConfig } from 'hooks/use-config';
import { ApiClient } from 'lib/api/client';
import {
  ListingOrgSoldRequest,
  ListingOrgSoldTypeEnum,
  ListingRead,
} from 'lib/api/generated';
import {
  getFormFieldErrorsFromResponse,
  getListingStatusLabel,
  getPriceTypeData,
} from 'lib/helpers';
import { useStore } from 'lib/store';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import * as yup from 'yup';

export interface MarkAsSoldDialogProps extends DialogProps {
  onDismiss?: () => void;
  closeMarkAsSoldDialog?: Function;
  listingDetails: ListingRead;
  updateSaleDetails?: Function;
  refetch?: Function;
}

const PriceType = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${globalTheme.space[1]};
`;

const Label = styled(Text)`
  margin-top: ${globalTheme.space[2]};
`;

const schema = yup.object().shape({
  sold_price_type: yup
    .mixed()
    .oneOf(['LIVEWEIGHT', 'HSCW', 'PER_HEAD'])
    .required('Please select a price type'),
  sold_price: yup
    .number()
    .typeError('Please enter a price')
    .required('Please enter a price')
    .min(0, 'Please enter a valid price')
    .test(
      'two-decimal-places',
      'Please enter a valid price with no more than 2 decimal places',
      function (value) {
        if (value === undefined) {
          return false; // Validation fails if value is undefined
        }
        return /^\d+(\.\d{1,2})?$/.test(value.toString());
      }
    ),
});

export const MarkAsSoldDialog: FC<MarkAsSoldDialogProps> = ({
  isOpen,
  closeMarkAsSoldDialog,
  listingDetails,
  updateSaleDetails,
  refetch,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ListingOrgSoldRequest>({
    resolver: yupResolver(schema),
  });

  const activeBuyerOrgId = useStore((store) => store.activeBuyerOrgId);
  const { data: config } = useConfig();

  useEffect(() => {
    if (listingDetails && reset) {
      reset({
        sold_price_type: listingDetails?.sold_price_type,
        sold_price: listingDetails?.sold_price,
        sold_hide_price: listingDetails?.sold_hide_price || undefined,
        sold_buyer: listingDetails?.sold_buyer,
      });
    }
  }, [listingDetails, reset]);

  const createMarkAsSold = useMutation(
    (listingOrgSoldRequest: ListingOrgSoldRequest) =>
      new ApiClient().listingApi.orgMarketplaceListingSoldCreate({
        buyerOrgId: activeBuyerOrgId || '',
        listingId: listingDetails?.id as string,
        listingOrgSoldRequest,
      })
  );

  const destroyMarkAsSold = useMutation(() =>
    new ApiClient().listingApi.orgMarketplaceListingSoldDestroy({
      buyerOrgId: activeBuyerOrgId || '',
      listingId: listingDetails?.id as string,
    })
  );
  const destroyUnderOffer = useMutation(() =>
    new ApiClient().listingApi.orgMarketplaceListingUnderOfferDestroy({
      buyerOrgId: activeBuyerOrgId || '',
      listingId: listingDetails?.id as string,
    })
  );

  const onSubmit = async (
    data: ListingOrgSoldRequest,
    type: ListingOrgSoldTypeEnum
  ) => {
    try {
      await createMarkAsSold.mutateAsync({ ...data, type }).then((res) => {
        updateSaleDetails?.(res.data);
        refetch?.();
      });
      toast.success(
        listingDetails?.sold_price_type
          ? 'Filled details updated successfully'
          : 'Filled details added successfully'
      );
      closeMarkAsSoldDialog?.(true);
    } catch (error) {
      if (!axios.isAxiosError(error)) return;

      if (error.response) {
        // Handle server errors
        if (error.response.status === 500) {
          toast.error('Something has gone wrong, please try again later');
        }

        // Handle validation errors
        if (error.response.data.code === 'VALIDATION_ERROR') {
          // Map errors returned to relevant form fields
          const errors = getFormFieldErrorsFromResponse<FormData>(
            error.response.data.errors
          );
          errors.forEach(({ message }) => {
            toast.error(message);
          });
        }
      }
    }
  };

  const handleRemoveSold = async () => {
    try {
      if (listingDetails?.sold) {
        await destroyMarkAsSold.mutateAsync().then((res) => {
          updateSaleDetails?.(res.data);
          reset();
          refetch?.();
        });
      } else if (listingDetails?.under_offer) {
        await destroyUnderOffer.mutateAsync().then((res) => {
          updateSaleDetails?.(res.data);
          reset();
          refetch?.();
        });
      }

      toast.success('Relisted sucessfully');
      closeMarkAsSoldDialog?.(true);
    } catch (error) {
      if (!axios.isAxiosError(error)) return;

      if (error.response) {
        // Handle server errors
        if (error.response.status === 500) {
          toast.error('Something has gone wrong, please try again later');
        }
      }
    }
  };

  const onCloseDialog = () => {
    reset();
    closeMarkAsSoldDialog?.(true);
  };

  return (
    <Dialog
      title={'Congratulations'}
      onDismiss={onCloseDialog}
      isOpen={isOpen}
      actions={
        <>
          <Button
            size='small'
            onClick={() =>
              handleSubmit((data) =>
                onSubmit(data, ListingOrgSoldTypeEnum.UnderOffer)
              )()
            }
            isLoading={
              createMarkAsSold.isLoading &&
              createMarkAsSold.variables?.type ===
                ListingOrgSoldTypeEnum.UnderOffer
            }
            variant={listingDetails?.under_offer ? 'primary' : 'outline'}
            disabled={createMarkAsSold.isLoading}
          >
            {listingDetails?.under_offer
              ? 'Update Under Offer'
              : 'Mark Under Offer'}
          </Button>

          <Button
            size='small'
            onClick={() =>
              handleSubmit((data) =>
                onSubmit(data, ListingOrgSoldTypeEnum.Sold)
              )()
            }
            disabled={createMarkAsSold.isLoading}
            variant='tertiary'
            isLoading={
              createMarkAsSold.isLoading &&
              createMarkAsSold.variables?.type === ListingOrgSoldTypeEnum.Sold
            }
          >
            {listingDetails?.sold
              ? 'Update Sale Details'
              : `Mark as ${getListingStatusLabel(listingDetails?.type)}`}
          </Button>

          <Button
            size='small'
            variant='outline'
            onClick={handleRemoveSold}
            disabled={!listingDetails?.sold && !listingDetails?.under_offer}
            isLoading={
              destroyMarkAsSold.isLoading || destroyUnderOffer.isLoading
            }
            data-cy='backButton'
          >
            Relist
          </Button>
        </>
      }
    >
      <form>
        {(listingDetails?.sold || listingDetails?.under_offer) && (
          <Message
            backgroundColor={
              listingDetails?.under_offer ? 'lightBlue' : 'lightRed'
            }
            $compact
            padding={3}
            textAlign='left'
          >
            <Flex alignItems='center' padding={0} gap={0} stackOnSmall>
              <AlertCircleSolid width={24} height={24} fill='white' />
              <Flex alignItems='center' direction='column'>
                <Text lineHeight='heading' fontSize={2} fontWeight='bold'>
                  This listing is currently{' '}
                  {listingDetails?.sold
                    ? getListingStatusLabel(listingDetails?.type).toLowerCase()
                    : 'under offer'}
                  .
                </Text>
              </Flex>
            </Flex>
          </Message>
        )}
        <Stack space={0}>
          <Label fontWeight='bold' aria-label={'price_type_label'}>
            Price Type
          </Label>
          <PriceType>
            {config?.price_types?.map((priceType, index) => (
              <RadioBox
                key={index}
                data-cy={`${priceType?.price_type_id}Box`}
                label={getPriceTypeData(priceType?.price_type_id).name}
                value={priceType?.price_type_id}
                {...register('sold_price_type')}
              />
            ))}
          </PriceType>
          {errors?.sold_price_type && (
            <ErrorMessage>{errors?.sold_price_type?.message}</ErrorMessage>
          )}
          <Label fontWeight='bold' aria-label={'sale_price'}>
            Price
          </Label>
          <Input
            type='number'
            {...register('sold_price')}
            icon='DollarSignIcon'
          />
          {errors?.sold_price && (
            <ErrorMessage>{errors?.sold_price?.message}</ErrorMessage>
          )}

          <Flex alignItems='center' gap={0}>
            <Checkbox
              defaultChecked={listingDetails?.sold_hide_price || undefined}
              data-cy={`hide-sale-price-to-agora`}
              {...register(`sold_hide_price`)}
            />
            <Text fontWeight='bold' aria-label={'hide_sale_price'}>
              Hide the price from display on the Agora marketplace?{' '}
            </Text>
          </Flex>
          <Label fontWeight='bold' aria-label={'buyer_name'}>
            Buyer name (Optional)
          </Label>
          <Text aria-label={'buyer_name_content'}>
            This is for your records only and
            <Text as='span' fontWeight='bold'>
              {' '}
              will not
            </Text>{' '}
            be displayed on the Marketplace listing.
          </Text>
          <Input type='text' {...register('sold_buyer')} />
        </Stack>
      </form>
    </Dialog>
  );
};
